import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  isBaseDeviationType,
  type BaseDeviationRunSummary,
  type BaseDeviationSideReadiness,
  type ContractDetail,
  type ListBaseDeviationReportsResponse,
} from '@clm/shared-types';

import { listBaseDeviationReports, runBaseDeviationReport } from '../api/base-deviation-reports';
import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { mayAttempt } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { ErrorNotice } from '../components/ErrorNotice';
import {
  BASE_DEVIATION_PROVISIONAL_COPY,
  BASE_DEVIATION_READER_ROLES,
  NO_BASE_COPY,
  NO_VISIBLE_BASE_COPY,
  SUBJECT_REFUSAL_COPY,
  baseDeviationReportPath,
  readinessCopy,
  runProblemOf,
  type RunProblem,
} from './base-deviation-shared';

/**
 * "Compare with base MSA" on the contract record — CLM-112 (FR-38, NFR-18,
 * R-112-16).
 *
 * **Offered, never forced.** Rendered for a contract of an enabled base type
 * (MSA) that is not an amendment — `lineage.amends_contract_key`, because an
 * MSA amendment carries its parent's type — and only to a sign-in that may
 * read it (`mayAttempt`, Admin and Legal). A 403 hides it quietly, the CLM-111
 * rule: it is an auxiliary read, and the page's own banner is about the
 * contract. Any other failure is this card's own notice.
 *
 * **Readiness in the page's own words.** Each side's state (R-112-10) is a
 * sentence, and **Run report** is enabled only when both are ready; a `409`
 * or `400` on Run is the same sentence, never `ErrorNotice`'s copy for the kind
 * (it is written for other surfaces — for a 409, verify-field's "another
 * reviewer settled this field").
 *
 * **Under a practices scope, "no base" is "none visible"** (R-112-12): a report
 * needs both contracts in scope, so the card says so rather than suggesting
 * none exists. CLM-111's `scopedWithoutBaseCopy` is nomination copy and is not
 * reused.
 *
 * **Past runs** are listed with the base version each was computed against,
 * their counts, and a live stale flag — a run is never re-computed in place;
 * Run again makes a new one (R-112-8).
 *
 * **A provisional type says so** (CLM-110). The section keys on
 * `contract_type`, which is the file name's guess or a person's hint until an
 * administrator confirms it; on a provisional MSA one line says the report
 * assumes it is an MSA. Nothing else changes: a report already run is pinned to
 * the type it ran under (§3.5).
 *
 * **Re-read when the base moves.** `baseGeneration` is the record's signal
 * that its Set / Replace base action (CLM-111) nominated a base, or learned
 * that someone else had: the current base, its readiness and the subject's
 * refusal all depend on it, so the card reads them again rather than offering
 * Run against a base that is no longer current — or against this contract
 * after it became the base itself.
 */
type Phase =
  | { readonly kind: 'loading' }
  | { readonly kind: 'hidden' }
  | { readonly kind: 'loaded'; readonly response: ListBaseDeviationReportsResponse }
  | { readonly kind: 'problem'; readonly problem: ApiProblemKind; readonly correlation_id: string | null };

export function BaseDeviationSection({
  contract,
  baseGeneration,
}: {
  readonly contract: ContractDetail;
  /** Bumped by the record when the base moves; each change re-reads the card. */
  readonly baseGeneration: number;
}) {
  const { identity } = useIdentity();
  const navigate = useNavigate();
  const offered =
    mayAttempt(identity, BASE_DEVIATION_READER_ROLES) &&
    isBaseDeviationType(contract.contract_type) &&
    contract.lineage.amends_contract_key === null;
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [running, setRunning] = useState(false);
  const [runProblem, setRunProblem] = useState<RunProblem | null>(null);

  useEffect(() => {
    if (!offered) return;
    const controller = new AbortController();
    setPhase({ kind: 'loading' });
    /* A refused Run was about the base as it was; it says nothing about the one re-read now. */
    setRunProblem(null);
    void listBaseDeviationReports(contract.contract_key, controller.signal).then(
      (response) => {
        if (!controller.signal.aborted) setPhase({ kind: 'loaded', response });
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof ApiError && error.isForbidden) {
          setPhase({ kind: 'hidden' });
          return;
        }
        setPhase({
          kind: 'problem',
          problem: classifyApiError(error),
          correlation_id: error instanceof ApiError ? error.correlation_id : null,
        });
      },
    );
    return () => {
      controller.abort();
    };
  }, [offered, contract.contract_key, baseGeneration]);

  if (!offered || phase.kind === 'hidden') return null;

  async function run(nominationId: string) {
    setRunning(true);
    setRunProblem(null);
    try {
      const response = await runBaseDeviationReport(contract.contract_key, nominationId);
      navigate(baseDeviationReportPath(contract.contract_key, response.report.report_id));
    } catch (error) {
      setRunProblem(runProblemOf(error));
      setRunning(false);
    }
  }

  return (
    <div className="card" data-testid="base-deviation">
      <h3>Compare with base MSA</h3>
      {/* CLM-110 (§4.7) — the report keys on the type, which is provisional until an administrator confirms it. */}
      {contract.contract_type_status.confirmation === 'provisional' ? (
        <p className="mut" data-testid="base-deviation-provisional">
          {BASE_DEVIATION_PROVISIONAL_COPY}
        </p>
      ) : null}
      {phase.kind === 'loading' ? (
        <p role="status" className="mut">
          Reading the base MSA and this contract’s readiness…
        </p>
      ) : null}
      {phase.kind === 'problem' ? <ErrorNotice kind={phase.problem} correlation_id={phase.correlation_id} /> : null}
      {phase.kind === 'loaded' ? (
        <Loaded
          contractKey={contract.contract_key}
          response={phase.response}
          running={running}
          runProblem={runProblem}
          onRun={(nominationId) => void run(nominationId)}
        />
      ) : null}
    </div>
  );
}

function Loaded({
  contractKey,
  response,
  running,
  runProblem,
  onRun,
}: {
  readonly contractKey: string;
  readonly response: ListBaseDeviationReportsResponse;
  readonly running: boolean;
  readonly runProblem: RunProblem | null;
  readonly onRun: (nominationId: string) => void;
}) {
  const { subject, current_base: base } = response;
  const refusal = subject.refusal;

  return (
    <>
      <p className="mut">
        A join through the clause library: each clause of the base is Standard, Modified, missing or extra here, and
        whatever could not be compared is listed by name. No model is called, and nothing is redlined.
      </p>

      {base === null ? (
        <p data-testid="base-deviation-no-base">{response.scope_kind === 'practices' ? NO_VISIBLE_BASE_COPY : NO_BASE_COPY}</p>
      ) : (
        <>
          <p>
            Current base MSA:{' '}
            <Link to={`/contracts/record/${encodeURIComponent(base.nomination.contract_key)}`}>
              <code>{base.nomination.contract_key}</code>
            </Link>{' '}
            · v{base.nomination.version_no}
            {base.nomination.contract_title === null ? null : <> · {base.nomination.contract_title}</>}
          </p>
          {refusal !== null ? (
            <p className="banner up" role="status" data-testid="base-deviation-refusal" data-code={refusal}>
              {SUBJECT_REFUSAL_COPY[refusal]}
            </p>
          ) : (
            <Readiness subject={subject.readiness ?? 'ready'} base={base.readiness} />
          )}
          {subject.conversion_caveat === 'tables_missing' ? (
            <p className="mut">Tables were not detected when this contract was converted; the report will say so.</p>
          ) : null}
          {refusal === null ? (
            <span className="fl">
              <button
                type="button"
                className="btn sm pri"
                disabled={running || subject.readiness !== 'ready' || base.readiness !== 'ready'}
                onClick={() => onRun(base.nomination.nomination_id)}
              >
                {running ? 'Running…' : response.runs.length === 0 ? 'Run report' : 'Run again'}
              </button>
              <span className="mut">Against v{base.nomination.version_no}. Every run is kept; nothing is recomputed in place.</span>
            </span>
          ) : null}
          {runProblem === null ? null : <RunProblemNotice problem={runProblem} />}
        </>
      )}

      {response.runs.length === 0 ? null : <PastRuns contractKey={contractKey} runs={response.runs} />}
    </>
  );
}

function Readiness({ subject, base }: { readonly subject: BaseDeviationSideReadiness; readonly base: BaseDeviationSideReadiness }) {
  const lines = [readinessCopy('subject', subject), readinessCopy('base', base)].filter((line): line is string => line !== null);
  if (lines.length === 0) {
    return <p className="mut">Both contracts are converted, segmented and matched — the report can be run.</p>;
  }
  return (
    <div className="banner up" role="status" data-testid="base-deviation-readiness">
      <p className="notice-head">
        <strong>Not ready to compare</strong>
      </p>
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}

/** A refused Run: the page's own sentence for a named code, `ErrorNotice` by kind for anything else. */
function RunProblemNotice({ problem }: { readonly problem: RunProblem }) {
  if (problem.copy === null) return <ErrorNotice kind={problem.kind} correlation_id={problem.correlation_id} />;
  return (
    <div className={`banner ${problem.kind === 'validation' ? 'bad' : 'up'}`} role="status" data-testid="base-deviation-run-refusal" data-code={problem.code ?? ''}>
      <p className="notice-head">
        <strong>Not run — nothing was stored</strong>
      </p>
      <p>{problem.copy}</p>
      {problem.correlation_id === null || problem.correlation_id === '' ? null : (
        <p className="mut">
          Correlation id <code>{problem.correlation_id}</code>
        </p>
      )}
    </div>
  );
}

function PastRuns({ contractKey, runs }: { readonly contractKey: string; readonly runs: readonly BaseDeviationRunSummary[] }) {
  return (
    <div data-testid="base-deviation-runs">
      <h4>Past reports</h4>
      <ul className="plain">
        {runs.map((run) => {
          const lines = Object.values(run.summary.lines_by_category).reduce((sum, n) => sum + n, 0);
          const notCompared = run.summary.base.not_compared + run.summary.subject.not_compared;
          const stale = [
            run.nomination_is_current ? null : 'base replaced since',
            run.stale.text_changed.base || run.stale.text_changed.subject ? 'text changed since' : null,
            run.stale.matches_changed ? 'matches changed since' : null,
          ].filter((chip): chip is string => chip !== null);
          return (
            <li key={run.report_id} data-testid="base-deviation-run">
              <Link to={baseDeviationReportPath(contractKey, run.report_id)}>Report {run.report_id}</Link> · against{' '}
              <code>{run.base.contract_key}</code> v{run.base.version_no} · {run.computed_utc} by {run.requested_by_upn} ·{' '}
              {lines} lines, {notCompared} paragraphs not compared{' '}
              {stale.map((chip) => (
                <span key={chip} className="tag acc">
                  {chip}
                </span>
              ))}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
